/**
 * Tree Entity Configuration
 *
 * @version 1.0
 * @module core/modules/environmental/config
 *
 * @description
 * Configuración de la entidad "Árbol" para el entity registry.
 * Define API, UI, factory y metadatos para árboles.
 */

import { TreeDeciduous } from 'lucide-react';
import type { EntityConfig } from '../../../entity-registry';
import type { Tree } from '../../../map-tools/tree-types';
import { TreeDetailsPanel } from '../../../map-tools/TreeDetailsPanel';
import { treesApi } from '../../../map-tools/trees-api';

/**
 * Crea un árbol con valores por defecto
 */
function createDefaultTree(coordinates: [number, number]): Tree {
  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).substring(2, 15);

  return {
    id: `tree-${timestamp}-${randomSuffix}`,
    code: `TEMP-TREE-${timestamp}`,
    coordinates,
    createdAt: new Date(),
    registryStatus: 'active',

    // Datos básicos
    commonName: undefined,
    scientificName: undefined,
    family: undefined,
    genus: undefined,
    species: undefined,

    // Estado
    healthStatus: 'good',
    structuralCondition: 'good',
    phenologicalState: 'normal',

    // Mediciones
    height: undefined,
    trunkDiameter: undefined,
    crownDiameter: undefined,

    // Riesgo
    riskLevel: 'low',
    proximityToBuildings: false,
    proximityToRoads: false,
    proximityToPowerLines: false,

    // Mantenimiento
    lastMaintenanceDate: undefined,
    nextMaintenanceDate: undefined,
    maintenanceHistory: [],

    // Patrimonio
    heritageValue: 'low',
    protectionStatus: 'none',
    age: undefined,
    origin: 'planted',

    // Metadata
    lastUpdate: new Date(),
    updatedBy: 'Sistema',
  };
}

/**
 * Configuración de la entidad "Árbol"
 */
export const treeEntityConfig: EntityConfig<Tree> = {
  type: 'tree',
  displayName: 'árbol',
  toolId: 'create-tree',

  apiService: {
    create: treesApi.create.bind(treesApi),
    update: treesApi.update.bind(treesApi),
    delete: treesApi.delete.bind(treesApi),
  },

  detailsPanel: TreeDetailsPanel,
  icon: TreeDeciduous,

  createDefault: createDefaultTree,
  tempIdPrefix: 'tree-',
  codePrefix: 'TR',

  // Funciones extractoras (opcionales, usan los defaults)
  getId: (tree) => tree.id,
  getCoordinates: (tree) => tree.coordinates,
  getCode: (tree) => tree.code,
};
